import React, { useEffect, useState } from "react";
import InputBox from "../../../components/InputBox";
import useCustomer from "../../../hooks/useCustomer";
import { CustomerData, ProductDetail } from "../../../../../shared/services/interface";
import { getLedgersCustomer, postCustomer } from "../../../../../shared/services/services";
import { notifySuccess } from "../../../../../assets/toastService";
import { Button } from "src/business-types/grocery/components/Button";
import { FontColor, ThemeBasedColorHeader } from "src/shared/modules/profile/theme/themeCheacker";
import { errorHandling } from "src/shared/services/errorHandling";
import { formLabelClasses } from "@mui/material";

type Props = {
  customerDetails: (data: CustomerData | "") => void;
  clearData: string;
  selectProductList: ProductDetail[];
};

const CustomerSelection: React.FC<Props> = ({ customerDetails, clearData, selectProductList }) => {
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [showMore, setShowMore] = useState<boolean>(false);
  const [foundCustomer, setFoundCustomer] = useState<CustomerData | null>(null);
  const [reFresh, setReFresh] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [formData, setFormData] = useState({
    customeraddress1: "",
    customeraddress2: "",
    customeraddress3: "",
    alternativenumber: "",
    Email: "",
    osb: "",
  });
  const [error, setError] = useState({ Email: "", phoneNumber: "" });

  const { onlyCustomer } = useCustomer(reFresh, false, 1, 10, phoneNumber.length > 2 ? phoneNumber : "");

  const handlePhoneChange = (number: string) => {
    const sanitized = number.trim().replace(/\D/g, "");
    if (sanitized.length <= 12) setPhoneNumber(sanitized);
    if (sanitized.length < 4) {
      setFoundCustomer(null);
      setName("");
    }
  };

  useEffect(() => {
    if (onlyCustomer.length === 1) {
      const phoneLength = phoneNumber.length;
      const getData = onlyCustomer.map((i) => i.phone_number.length)[0];
      console.log(phoneLength, getData);

      const customerEqual = phoneLength === getData || onlyCustomer.find((i) => i.phone_number === phoneNumber);
      // console.log(customerEqual)
      if (customerEqual) {
        handleSelectCustomer(onlyCustomer[0]);
      } else {
        setFoundCustomer(null);
      }
    } else if (phoneNumber === "0000000000") {
      setName("XXXX");
    }
  }, [phoneNumber, onlyCustomer]);

  /** 🔽 Handle customer selection from dropdown */
  const handleSelectCustomer = async (selected: CustomerData) => {
    setFoundCustomer(selected);

    try {
      const ledger = await getLedgersCustomer(selected._id.$oid);
      setFoundCustomer({
        ...selected,
        Out_standing_balance: ledger.data?.ledger?.total_outstanding_balance ?? 0,
      });
    } catch {
      setFoundCustomer(selected);
    }
  };

  /** 💾 Add new customer */
  const handleConfirmNew = async () => {
    const newCustomer = {
      Name: name,
      Email: formData.Email,
      phone_number: phoneNumber,
      alternativenumber: formData.alternativenumber,
      Out_standing_balance: Number(formData.osb) ?? 0,
      Address: {
        customeraddress1: formData.customeraddress1,
        customeraddress2: formData.customeraddress2,
        customeraddress3: formData.customeraddress3,
      },
    };

    if (!newCustomer.phone_number) return;

    try {
      await postCustomer(newCustomer);
      notifySuccess("Customer added successfully");
      setReFresh(!reFresh);
      setShowMore(false);
    } catch (err) {
      errorHandling(err);
      console.error(err);
    }
  };

  /** 🧹 Reset customer form */
  const resetForm = () => {
    setFormData({
      customeraddress1: "",
      customeraddress2: "",
      customeraddress3: "",
      alternativenumber: "",
      Email: "",
      osb: "",
    });
    setPhoneNumber("");
    setName("");
    setFoundCustomer(null);
    customerDetails("");
  };

  /** 🔄 Clear when triggered externally */
  useEffect(() => {
    resetForm();
  }, [clearData]);

  /** 🧾 Send selected customer info to parent */
  useEffect(() => {
    customerDetails(foundCustomer || "");
  }, [foundCustomer]);

  /** 🧼 Auto clear if only one product selected */
  useEffect(() => {
    if (selectProductList.length === 1) resetForm();
  }, [selectProductList]);

  /** 🧱 Render input field helper */
  const renderInput = (label: string, value: string, key: keyof typeof formData) => (
    <div className='w-full p-2 flex justify-center items-center gap-2' key={key}>
      <p className='w-2/6'>{label}</p>
      <InputBox
        type='text'
        value={value}
        disabled={!!foundCustomer}
        maxLength={50}
        onChange={(e) => setFormData((prev) => ({ ...prev, [key]: e.target.value }))}
      />
    </div>
  );

  return (
    <div className='w-full bg-white border border-dashed border-black p-1 rounded-lg shadow-md'>
      {/* 🔹 Header */}
      <div className={`w-full py-1 text-lg font-bold rounded-xl flex justify-between items-center px-3 border ${ThemeBasedColorHeader()}`}>
        <div className='flex gap-2'>
          <button onClick={() => setIsOpen((prev) => !prev)} className='cursor-pointer'>
            {isOpen ? "▲" : "▼"}
          </button>

          <h1>Customer Selection</h1>
        </div>

        {!phoneNumber && (
          <button
            onClick={() => setPhoneNumber("0000000000")}
            className={`border bg-white shadow-md py-1 px-2 rounded-md ${FontColor()} disabled:opacity-50 disabled:cursor-not-allowed`}
            disabled={selectProductList.length === 1}
          >
            Unknown Customer
          </button>
        )}
      </div>
      {isOpen && (
        <>
          {/* 📞 Phone Number */}
          <div className='w-full p-2 flex justify-center items-center gap-2'>
            <p className='w-2/6'>Ph. Number</p>
            <InputBox
              type='number'
              placeholder='Search or Add Ph.Number'
              value={foundCustomer?.phone_number ?? phoneNumber}
              onChange={(e) => handlePhoneChange(e.target.value)}
              min={0}
              maxLength={12}
              disabled={selectProductList.length === 1}
            />
          </div>

          {/* 👤 Customer Name */}
          <div className='w-full relative p-2 flex justify-center items-center gap-2'>
            <p className='w-2/6'>Name</p>
            <div className='w-full relative'>
              <InputBox
                type='text'
                value={foundCustomer?.Name ?? name}
                onChange={(e) => {
                  setName(e.target.value);
                }}
                disabled={!!foundCustomer || selectProductList.length === 1 || phoneNumber.length < 7 || name === "XXXX"}
                maxLength={50}
              />
              {/* 🔽 Dropdown list */}
              {phoneNumber.length > 1 && onlyCustomer?.length > 0 && !foundCustomer && !name && (
                <div className='absolute z-10 w-full bg-white border border-gray-300 mt-1 rounded-md shadow-lg max-h-40 overflow-y-auto'>
                  {onlyCustomer.map((cust: CustomerData, index: number) => (
                    <div key={index} onClick={() => handleSelectCustomer(cust)} className='p-2 cursor-pointer hover:bg-blue-100'>
                      <p className='font-semibold'>{cust.Name}</p>
                      <p className='text-sm text-gray-500'>{cust.phone_number}</p>
                    </div>
                  ))}
                  {onlyCustomer.length === 0 && <div className='p-2 text-gray-500 text-center'>No customer found</div>}
                </div>
              )}
            </div>
          </div>

          {/* ➕ Additional Fields */}
          {!foundCustomer && (
            <div className='w-full px-2'>
              <div className='w-full flex justify-end'>
                {selectProductList.length !== 1 && (
                  <p className='cursor-pointer text-blue-500 hover:underline hover:text-blue-700' onClick={() => setShowMore(!showMore)}>
                    {showMore ? "Hide" : "+ More Details"}
                  </p>
                )}
              </div>

              {showMore && (
                <div>
                  <div className='w-full p-2 flex justify-center items-center gap-2'>
                    <p className='w-2/6'>Alt. Number</p>
                    <InputBox
                      type='number'
                      placeholder='Alt. Number'
                      value={formData.alternativenumber}
                      onChange={(e) => setFormData((prev) => ({ ...prev, alternativenumber: e.target.value }))}
                      min={0}
                      maxLength={12}
                    />
                  </div>
                  <div className='w-full p-2 flex justify-center items-center gap-2'>
                    <p className='w-2/6'>Prv. Balance</p>
                    <InputBox
                      type='number'
                      placeholder='Prev. Balance'
                      value={formData.osb}
                      onChange={(e) => setFormData((prev) => ({ ...prev, osb: e.target.value }))}
                      min={0}
                      maxLength={12}
                    />
                  </div>
                  <p className='text-end text-red-400'>{error.phoneNumber}</p>
                  {renderInput("Email", formData.Email, "Email")}
                  <p className='text-end text-red-400'>{error.Email}</p>
                  {renderInput("Address 1", formData.customeraddress1, "customeraddress1")}
                  {renderInput("Address 2", formData.customeraddress2, "customeraddress2")}
                  {renderInput("Address 3", formData.customeraddress3, "customeraddress3")}
                </div>
              )}

              <div className='w-full flex justify-center items-center mt-2'>
                <Button label='Confirm' onClick={handleConfirmNew} variant='ThemeBase' disabled={phoneNumber.length < 7} />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CustomerSelection;
